import { supabase, supabaseUrl } from './supabase';

// =====================================================
// PROFILES SERVICES
// =====================================================

export const profileService = {
  // Get current user profile
  async getProfile() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    
    if (error) {
      // If no profile found, create one
      if (error.code === 'PGRST116') { // No rows returned
        return await this.createProfile({
          id: user.id,
          full_name: user.user_metadata?.full_name || '',
          first_name: user.user_metadata?.first_name || '',
          last_name: user.user_metadata?.last_name || '',
          timezone: 'UTC',
          notifications: {
            new_replies: true,
            campaign_completion: true,
            weekly_reports: true,
            account_warnings: true,
            daily_limits: true,
            new_features: false
          }
        });
      }
      throw error;
    }
    return data;
  },

  // Update user profile
  async updateProfile(updates) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id)  // Explicitly specify which profile to update
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Create profile (using upsert to avoid duplicate key errors)
  async createProfile(profileData) {
    const { data, error } = await supabase
      .from('profiles')
      .upsert([{
        id: profileData.id,
        full_name: profileData.full_name || '',
        first_name: profileData.first_name || '',
        last_name: profileData.last_name || '',
        company_name: profileData.company_name || '',
        phone: profileData.phone || '',
        timezone: profileData.timezone || 'UTC',
        notifications: profileData.notifications || {
          new_replies: true,
          campaign_completion: true,
          weekly_reports: true,
          account_warnings: true,
          daily_limits: true,
          new_features: false
        }
      }], { 
        onConflict: 'id',
        ignoreDuplicates: false 
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Update user password (requires current password)
  async updatePassword(currentPassword, newPassword) {
    // First verify the current password by trying to sign in
    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.email) throw new Error('User email not found');

    // Verify current password by attempting to sign in
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: user.email,
      password: currentPassword
    });

    if (signInError) {
      throw new Error('Current password is incorrect');
    }

    // If current password is correct, update to new password
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword
    });
    
    if (error) throw error;
    return data;
  }
};

// =====================================================
// INSTAGRAM ACCOUNTS SERVICES
// =====================================================

export const instagramAccountService = {
  // Get all user's Instagram accounts with session/heartbeat data
  async getAccounts() {
    // First get the user's timezone
    const { data: { user } } = await supabase.auth.getUser();
    let userTimezone = 'America/Chicago'; // Default
    
    if (user) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('timezone')
        .eq('id', user.id)
        .single();
      
      if (profile?.timezone) {
        userTimezone = profile.timezone;
      }
    }
    
    const { data, error } = await supabase
      .from('instagram_accounts')
      .select(`
        *,
        extension_sessions (
          id,
          session_token,
          is_active,
          last_heartbeat,
          last_activity_at,
          extension_version,
          messages_sent_today,
          automation_enabled,
          browser_info,
          created_at
        )
      `)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    // Fetch message statistics for each account
    const accountsWithStats = await Promise.all(data.map(async (account) => {
      // Get total messages sent by this account
      const { count: totalMessages } = await supabase
        .from('automation_tasks')
        .select('*', { count: 'exact', head: true })
        .eq('instagram_account_id', account.id)
        .eq('status', 'completed');
      
      // Get messages sent today in user's timezone
      // We fetch all completed tasks and filter client-side for timezone accuracy
      const { data: completedTasks } = await supabase
        .from('automation_tasks')
        .select('completed_at')
        .eq('instagram_account_id', account.id)
        .eq('status', 'completed');
      
      // Calculate today's count based on user's timezone
      const todayMessages = completedTasks ? 
        completedTasks.filter(task => {
          const completedDate = new Date(task.completed_at);
          const userDate = new Date(completedDate.toLocaleString("en-US", {timeZone: userTimezone}));
          const today = new Date(new Date().toLocaleString("en-US", {timeZone: userTimezone}));
          today.setHours(0, 0, 0, 0);
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          
          return userDate >= today && userDate < tomorrow;
        }).length : 0;
      
      // Get the most recent active session
      const activeSessions = account.extension_sessions?.filter(session => session.is_active) || [];
      const latestSession = activeSessions.length > 0 
        ? activeSessions.sort((a, b) => new Date(b.last_heartbeat) - new Date(a.last_heartbeat))[0]
        : null;
      
      return {
        ...account,
        total_messages_sent: totalMessages || 0,
        messages_sent_today: todayMessages || 0,
        latestSession
      };
    }));
    
    // Process the data to include session status
    return accountsWithStats.map(account => {
      const latestSession = account.latestSession;
      
      // Recalculate activeSessions within this scope
      const activeSessions = account.extension_sessions?.filter(session => session.is_active) || [];

      // Calculate session status
      let sessionStatus = 'offline';
      let lastHeartbeat = null;
      let automationEnabled = false;
      
      if (latestSession) {
        lastHeartbeat = new Date(latestSession.last_heartbeat);
        const timeSinceHeartbeat = Date.now() - lastHeartbeat.getTime();
        
        // Consider online if heartbeat within last 2 minutes
        if (timeSinceHeartbeat < 2 * 60 * 1000) {
          sessionStatus = 'online';
        } else if (timeSinceHeartbeat < 10 * 60 * 1000) {
          sessionStatus = 'away';
        } else {
          sessionStatus = 'offline';
        }
        
        // Get automation status from the session
        automationEnabled = latestSession.automation_enabled || false;
      }

      return {
        ...account,
        session: {
          status: sessionStatus,
          lastHeartbeat: lastHeartbeat,
          automationEnabled: automationEnabled,
          activeSessions: activeSessions.length,
          extensionVersion: latestSession?.extension_version || null,
          messagesSentToday: latestSession?.messages_sent_today || account.messages_sent_today || 0,
          browserInfo: latestSession?.browser_info || null,
          timeSinceLastHeartbeat: lastHeartbeat ? Math.floor((Date.now() - lastHeartbeat.getTime()) / 1000) : null
        }
      };
    });
  },

  // Add new Instagram account
  async addAccount(accountData) {
    const { data, error } = await supabase
      .from('instagram_accounts')
      .insert([{
        username: accountData.username,
        display_name: accountData.display_name,
        followers_count: accountData.followers_count || 0,
        following_count: accountData.following_count || 0,
        posts_count: accountData.posts_count || 0,
        avatar_url: accountData.avatar_url,
        is_active: accountData.is_active || true,
        is_connected: accountData.is_connected || false,
        status: accountData.status || 'pending'
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Update Instagram account
  async updateAccount(id, updates) {
    const { data, error } = await supabase
      .from('instagram_accounts')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Delete Instagram account
  async deleteAccount(id) {
    const { error } = await supabase
      .from('instagram_accounts')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  // Toggle account active status
  async toggleAccountStatus(id, isActive) {
    return this.updateAccount(id, { is_active: isActive });
  }
};

// =====================================================
// CAMPAIGNS SERVICES
// =====================================================

export const campaignService = {
  // Get all task stats with pagination support
  async getAllTaskStats() {
    let allTasks = [];
    let offset = 0;
    const limit = 1000; // Supabase max per query
    let hasMore = true;
    
    // Fetch all completed tasks with pagination
    while (hasMore) {
      const { data, error } = await supabase
        .from('automation_tasks')
        .select('campaign_id, status, is_followup, lead_id')
        .eq('status', 'completed')
        .range(offset, offset + limit - 1)
        .order('created_at', { ascending: true }); // Order for consistent pagination
      
      if (error) throw error;
      
      if (data) {
        allTasks = [...allTasks, ...data];
        hasMore = data.length === limit;
        offset += limit;
      } else {
        hasMore = false;
      }
    }
    
    // Group by campaign_id and calculate global stats
    const statsByCampaign = {};
    const globalStats = {
      uniqueLeads: new Set(),
      totalOpeners: 0,
      totalFollowups: 0
    };
    
    allTasks.forEach(task => {
      // Per-campaign stats
      if (!statsByCampaign[task.campaign_id]) {
        statsByCampaign[task.campaign_id] = {
          messages_sent: 0,
          followups_sent: 0,
          unique_leads: new Set()
        };
      }
      
      if (!task.is_followup) {
        statsByCampaign[task.campaign_id].messages_sent++;
        globalStats.totalOpeners++;
        if (task.lead_id) {
          statsByCampaign[task.campaign_id].unique_leads.add(task.lead_id);
          globalStats.uniqueLeads.add(task.lead_id);
        }
      } else {
        statsByCampaign[task.campaign_id].followups_sent++;
        globalStats.totalFollowups++;
      }
    });
    
    // Convert Sets to counts
    Object.keys(statsByCampaign).forEach(campaignId => {
      statsByCampaign[campaignId].leads_contacted = statsByCampaign[campaignId].unique_leads.size;
      statsByCampaign[campaignId].total_completed = 
        statsByCampaign[campaignId].messages_sent + statsByCampaign[campaignId].followups_sent;
      delete statsByCampaign[campaignId].unique_leads;
    });
    
    return {
      byCampaign: statsByCampaign,
      global: {
        uniqueLeadsContacted: globalStats.uniqueLeads.size,
        totalOpeners: globalStats.totalOpeners,
        totalFollowups: globalStats.totalFollowups,
        totalMessages: globalStats.totalOpeners + globalStats.totalFollowups
      }
    };
  },

  // Get all user's campaigns with account info and leads list data
  async getCampaigns() {
    const { data: campaigns, error } = await supabase
      .from('campaigns')
      .select(`
        *,
        campaign_accounts (
          id,
          is_enabled,
          messages_sent,
          instagram_accounts (
            id,
            username,
            display_name,
            avatar_url,
            is_active
          )
        ),
        campaign_messages (
          id,
          message_type,
          message_text,
          is_active,
          delay_days
        ),
        campaign_leads_lists (
          leads_lists (
            id,
            name,
            total_leads
          )
        )
      `)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    // Get ALL stats in one query instead of per-campaign queries
    const { byCampaign: allStats } = await this.getAllTaskStats();
    
    // Merge stats with campaigns
    const campaignsWithStats = campaigns.map(campaign => {
      const stats = allStats[campaign.id] || {
        messages_sent: 0,
        followups_sent: 0,
        leads_contacted: 0,
        total_completed: 0
      };
      
      return {
        ...campaign,
        messages_sent: stats.messages_sent,
        followups_sent: stats.followups_sent,
        leads_contacted: stats.leads_contacted,
        total_tasks_completed: stats.total_completed,
        leads_processed: stats.leads_contacted,
        // Check if campaign uses JIT (if it's active and has tasks)
        uses_jit: campaign.status === 'active' && stats.total_completed > 0
      };
    });
    
    return campaignsWithStats;
  },

  // Get single campaign with full details
  async getCampaign(id) {
    const { data, error } = await supabase
      .from('campaigns')
      .select(`
        *,
        campaign_accounts (
          id,
          is_enabled,
          messages_sent,
          instagram_accounts (
            id,
            username,
            display_name,
            avatar_url,
            is_active
          )
        ),
        campaign_leads_lists (
          leads_lists (
            id,
            name,
            total_leads
          )
        ),
        leads (
          id,
          instagram_username,
          display_name,
          status,
          messages_sent
        )
      `)
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data;
  },

  // Create new campaign
  async createCampaign(campaignData) {
    const user = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('campaigns')
      .insert([{
        user_id: user.data.user?.id,
        name: campaignData.name,
        description: campaignData.description,
        status: 'draft',
        daily_message_min: campaignData.daily_message_min || 20,
        daily_message_max: campaignData.daily_message_max || 50,
        send_time_start: campaignData.send_time_start || '09:00:00',
        send_time_end: campaignData.send_time_end || '17:00:00',
        timezone: campaignData.timezone || 'UTC',
        exclude_weekends: campaignData.exclude_weekends || false,
        exclude_holidays: campaignData.exclude_holidays || false,
        automation_settings: campaignData.automation_settings || {}
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Update campaign
  async updateCampaign(id, updates) {
    const { data, error } = await supabase
      .from('campaigns')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Delete campaign
  async deleteCampaign(id) {
    const { error } = await supabase
      .from('campaigns')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  // Start campaign
  async startCampaign(id) {
    return this.updateCampaign(id, { 
      status: 'active', 
      started_at: new Date().toISOString() 
    });
  },

  // Generate tasks for campaign (calls edge function)
  async generateCampaignTasks(campaignId, useJIT = true) {
    console.log('🔧 generateCampaignTasks called with campaignId:', campaignId, 'JIT mode:', useJIT);
    
    try {
      if (useJIT) {
        // Just-In-Time mode: Initialize campaign without generating tasks
        console.log('🚀 Using JIT mode - initializing campaign only');
        
        // Mark campaign as active
        await this.updateCampaign(campaignId, { 
          status: 'active',
          started_at: new Date().toISOString()
        });
        
        // Initialize progress tracking for all campaign accounts
        const { data: campaignAccounts } = await supabase
          .from('campaign_accounts')
          .select('instagram_account_id')
          .eq('campaign_id', campaignId);
        
        // Count total leads
        const { count: totalLeads } = await supabase
          .from('campaign_leads_lists')
          .select('*, leads_lists!inner(leads_list_items!inner(id))', { count: 'exact' })
          .eq('campaign_id', campaignId);
        
        if (campaignAccounts && campaignAccounts.length > 0) {
          // Initialize progress for each account
          const progressEntries = campaignAccounts.map(ca => ({
            campaign_id: campaignId,
            instagram_account_id: ca.instagram_account_id,
            total_leads: Math.ceil((totalLeads || 0) / campaignAccounts.length),
            leads_processed: 0,
            opener_a_count: 0,
            opener_b_count: 0,
            next_variant: 'A',
            status: 'active'
          }));
          
          await supabase
            .from('campaign_progress')
            .insert(progressEntries);
        }
        
        return {
          success: true,
          message: 'Campaign initialized with JIT task generation',
          mode: 'jit',
          stats: {
            total_leads: totalLeads || 0,
            accounts_assigned: campaignAccounts?.length || 0
          }
        };
      } else {
        // Legacy mode: Generate all tasks upfront
        console.log('📡 Using legacy mode - calling super-worker edge function...');
        
        const { data, error } = await supabase.functions.invoke('super-worker', {
          body: { campaign_id: campaignId }
        });

        console.log('📡 Edge function response - data:', data);
        console.log('📡 Edge function response - error:', error);

        if (error) {
          console.error('❌ Task generation error:', error);
          throw new Error(`Failed to generate tasks: ${error.message}`);
        }

        if (!data) {
          console.error('❌ No data returned from edge function');
          throw new Error('No response from task generation service');
        }

        if (!data.success) {
          console.error('❌ Edge function returned failure:', data);
          throw new Error(data.error || 'Task generation failed');
        }

        console.log('✅ Task generation successful:', data);
        return data;
      }
    } catch (err) {
      console.error('❌ Exception in generateCampaignTasks:', err);
      throw err;
    }
  },

  // Start campaign with task generation
  async startCampaignWithTasks(id) {
    console.log('🚀 startCampaignWithTasks called with id:', id);
    
    try {
      console.log(`🚀 Starting campaign ${id} with task generation...`);
      
      // Call the task generator edge function
      console.log('🔄 About to call generateCampaignTasks...');
      const result = await this.generateCampaignTasks(id);
      
      console.log('✅ Campaign started successfully:', result.stats);
      return result;
    } catch (error) {
      console.error('❌ Failed to start campaign:', error);
      // Revert campaign status if task generation failed
      console.log('🔄 Reverting campaign status to draft...');
      await this.updateCampaign(id, { status: 'draft' });
      throw error;
    }
  },

  // Get task generation status (legacy)
  async getTaskGenerationStatus(campaignId) {
    const { data, error } = await supabase
      .from('campaign_task_generation')
      .select('*')
      .eq('campaign_id', campaignId)
      .single();

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    return data || null;
  },

  // Get campaign progress (JIT mode)
  async getCampaignProgress(campaignId) {
    const { data, error } = await supabase
      .from('campaign_progress')
      .select('*')
      .eq('campaign_id', campaignId);

    if (error) {
      console.error('Error fetching campaign progress:', error);
      return [];
    }

    return data || [];
  },

  // Pause campaign
  async pauseCampaign(id) {
    return this.updateCampaign(id, { status: 'paused' });
  },

  // Invalidate reserved tasks when campaign settings change
  async invalidateReservedTasks(campaignId) {
    const { error } = await supabase
      .rpc('invalidate_campaign_reserved_tasks', { p_campaign_id: campaignId });
    
    if (error) throw error;
  },

  // Clean up all expired reserved tasks
  async cleanupExpiredTasks() {
    const { error } = await supabase
      .rpc('cleanup_expired_reserved_tasks');
    
    if (error) throw error;
  },

  // Get campaign statistics
  async getCampaignStats() {
    const { data, error } = await supabase
      .from('campaigns')
      .select(`
        status,
        messages_sent,
        responses_received,
        response_rate
      `);
    
    if (error) throw error;
    
    // Calculate aggregate stats
    const stats = {
      totalCampaigns: data.length,
      activeCampaigns: data.filter(c => c.status === 'active').length,
      totalMessagesSent: data.reduce((sum, c) => sum + c.messages_sent, 0),
      totalResponses: data.reduce((sum, c) => sum + c.responses_received, 0),
      averageResponseRate: data.length > 0 
        ? data.reduce((sum, c) => sum + c.response_rate, 0) / data.length 
        : 0
    };
    
    return stats;
  }
};

// =====================================================
// CAMPAIGN ACCOUNTS SERVICES
// =====================================================

export const campaignAccountService = {
  // Add account to campaign
  async addAccountToCampaign(campaignId, instagramAccountId) {
    const { data, error } = await supabase
      .from('campaign_accounts')
      .insert([{
        campaign_id: campaignId,
        instagram_account_id: instagramAccountId,
        is_enabled: true
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Remove account from campaign
  async removeAccountFromCampaign(campaignId, instagramAccountId) {
    const { error } = await supabase
      .from('campaign_accounts')
      .delete()
      .eq('campaign_id', campaignId)
      .eq('instagram_account_id', instagramAccountId);
    
    if (error) throw error;
  },

  // Toggle account enabled status in campaign
  async toggleAccountInCampaign(campaignId, instagramAccountId, isEnabled) {
    const { data, error } = await supabase
      .from('campaign_accounts')
      .update({ is_enabled: isEnabled })
      .eq('campaign_id', campaignId)
      .eq('instagram_account_id', instagramAccountId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Update account status (alias for toggleAccountInCampaign)
  async updateAccountStatus(campaignId, instagramAccountId, isEnabled) {
    return this.toggleAccountInCampaign(campaignId, instagramAccountId, isEnabled);
  },

  // Get accounts for campaign
  async getCampaignAccounts(campaignId) {
    const { data, error } = await supabase
      .from('campaign_accounts')
      .select(`
        *,
        instagram_accounts (
          id,
          username,
          display_name,
          avatar_url,
          is_active
        )
      `)
      .eq('campaign_id', campaignId);
    
    if (error) throw error;
    return data;
  }
};

// =====================================================
// CAMPAIGN MESSAGES SERVICES
// =====================================================

export const campaignMessageService = {
  // Get messages for a campaign
  async getCampaignMessages(campaignId) {
    const { data, error } = await supabase
      .from('campaign_messages')
      .select('*')
      .eq('campaign_id', campaignId)
      .order('message_type');
    
    if (error) throw error;
    return data;
  },

  // Add message to campaign
  async addCampaignMessage(messageData) {
    const { data, error } = await supabase
      .from('campaign_messages')
      .insert([{
        campaign_id: messageData.campaign_id,
        message_type: messageData.message_type,
        message_text: messageData.message_text,
        delay_days: messageData.delay_days || 0,
        ab_test_percentage: messageData.ab_test_percentage || 50,
        personalization_variables: messageData.personalization_variables || [],
        is_active: messageData.is_active !== false
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Update campaign message
  async updateCampaignMessage(id, updates) {
    const { data, error } = await supabase
      .from('campaign_messages')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Delete campaign message
  async deleteCampaignMessage(id) {
    const { error } = await supabase
      .from('campaign_messages')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  // Bulk create messages for campaign
  async createCampaignMessages(campaignId, messages) {
    const messageData = Object.entries(messages)
      .filter(([_, message]) => message.enabled && message.text)
      .map(([messageType, message]) => ({
        campaign_id: campaignId,
        message_type: messageType,
        message_text: message.text,
        delay_days: message.delay_days || 0,
        ab_test_percentage: messageType === 'opener_b' ? 50 : 100,
        is_active: true
      }));

    if (messageData.length === 0) return [];

    const { data, error } = await supabase
      .from('campaign_messages')
      .insert(messageData)
      .select();
    
    if (error) throw error;
    return data;
  }
};

// =====================================================
// LEADS SERVICES
// =====================================================

export const leadsService = {
  // Get leads for campaign
  async getLeads(campaignId, filters = {}) {
    let query = supabase
      .from('leads')
      .select('*')
      .eq('campaign_id', campaignId);
    
    if (filters.status) {
      query = query.eq('status', filters.status);
    }
    
    if (filters.search) {
      query = query.or(`instagram_username.ilike.%${filters.search}%,display_name.ilike.%${filters.search}%`);
    }
    
    const { data, error } = await query.order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  // Add new lead
  async addLead(leadData) {
    const { data, error } = await supabase
      .from('leads')
      .insert([leadData])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Update lead status
  async updateLeadStatus(id, status) {
    const { data, error } = await supabase
      .from('leads')
      .update({ status })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Get lead statistics
  async getLeadStats(campaignId) {
    const { data, error } = await supabase
      .from('leads')
      .select('status')
      .eq('campaign_id', campaignId);
    
    if (error) throw error;
    
    const stats = {
      total: data.length,
      pending: data.filter(l => l.status === 'pending').length,
      contacted: data.filter(l => l.status === 'contacted').length,
      responded: data.filter(l => l.status === 'responded').length,
      interested: data.filter(l => l.status === 'interested').length,
      notInterested: data.filter(l => l.status === 'not_interested').length
    };
    
    return stats;
  }
};

// =====================================================
// MESSAGES SERVICES
// =====================================================

export const messageService = {
  // Get sent messages for campaign
  async getSentMessages(campaignId) {
    const { data, error } = await supabase
      .from('sent_messages')
      .select(`
        *,
        instagram_accounts (
          username
        )
      `)
      .eq('campaign_id', campaignId)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  // Get received responses for campaign
  async getReceivedResponses(campaignId) {
    const { data, error } = await supabase
      .from('received_responses')
      .select(`
        *,
        instagram_accounts (
          username
        )
      `)
      .eq('campaign_id', campaignId)
      .order('received_at', { ascending: false });

    if (error) throw error;

    // Enrich with the lead behind each thread. received_responses has no FK to leads, but
    // lead_conversations maps instagram_conversation_id -> lead_username (+ A/B variant). Join in JS.
    const convIds = [...new Set((data || []).map((r) => r.instagram_conversation_id).filter(Boolean))];
    if (convIds.length) {
      const { data: convs } = await supabase
        .from('lead_conversations')
        .select('instagram_conversation_id, lead_username, ab_test_variant')
        .in('instagram_conversation_id', convIds);
      const byConv = Object.fromEntries((convs || []).map((c) => [c.instagram_conversation_id, c]));
      (data || []).forEach((r) => {
        const c = byConv[r.instagram_conversation_id];
        r.lead_username = c ? c.lead_username : null;
        r.ab_test_variant = c ? c.ab_test_variant : null;
      });
    }
    return data;
  },

  // Mark response as read
  async markResponseAsRead(id) {
    const { data, error } = await supabase
      .from('received_responses')
      .update({ is_read: true })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Get inbox summary
  async getInboxSummary() {
    const { data, error } = await supabase
      .from('received_responses')
      .select(`
        id,
        message_text,
        is_read,
        received_at,
        leads (
          instagram_username,
          display_name
        ),
        campaigns (
          name
        )
      `)
      .order('received_at', { ascending: false })
      .limit(50);
    
    if (error) throw error;
    return data;
  }
};

// =====================================================
// DASHBOARD STATS SERVICES
// =====================================================

export const dashboardService = {
  // Get overview statistics
  async getOverviewStats() {
    try {
      // Get all task stats (includes pagination)
      const { global: globalStats } = await campaignService.getAllTaskStats();
      
      // Get total leads from leads_lists
      const { data: leadLists } = await supabase
        .from('leads_lists')
        .select('total_leads');
      
      const totalLeads = leadLists ? 
        leadLists.reduce((sum, list) => sum + (list.total_leads || 0), 0) : 0;
      
      // Get Instagram accounts for connected accounts count
      const { data: accounts } = await supabase
        .from('instagram_accounts')
        .select('id, is_active')
        .eq('is_active', true);
      
      // Get campaigns for active count
      const { data: campaigns } = await supabase
        .from('campaigns')
        .select('id, status');
      
      return {
        totalLeads,
        messagesSent: globalStats.uniqueLeadsContacted, // This is leads contacted
        followupsSent: globalStats.totalFollowups,
        totalMessages: globalStats.totalMessages,
        leadsContacted: globalStats.uniqueLeadsContacted,
        // For backwards compatibility
        responses: 0, // We don't track responses yet
        responseRate: 0,
        connectedAccounts: accounts?.filter(acc => acc.is_active).length || 0,
        activeCampaigns: campaigns?.filter(c => c.status === 'active').length || 0,
        totalCampaigns: campaigns?.length || 0
      };
    } catch (error) {
      console.error('Error fetching overview stats:', error);
      throw error;
    }
  },

  // Get recent activity
  async getRecentActivity(limit = 10) {
    try {
      // Helper function to format time ago
      const formatTimeAgo = (dateString) => {
        const now = new Date();
        const date = new Date(dateString);
        const diffInSeconds = Math.floor((now - date) / 1000);
        
        if (diffInSeconds < 60) return 'Just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
        if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
        return date.toLocaleDateString();
      };
      
      const activities = [];
      
      // Get recent sent messages (lead messaged activities)
      const sentMessages = await supabase
        .from('sent_messages')
        .select(`
          id,
          sent_at,
          created_at,
          status,
          campaign_id,
          instagram_conversation_id
        `)
        .order('created_at', { ascending: false })
        .limit(Math.floor(limit / 3));
      
      if (sentMessages.data) {
        // Get campaign names for the messages
        const campaignIds = [...new Set(sentMessages.data.map(m => m.campaign_id).filter(Boolean))];
        const campaignsData = campaignIds.length > 0 ? await supabase
          .from('campaigns')
          .select('id, name')
          .in('id', campaignIds) : { data: [] };
        
        const campaignMap = {};
        if (campaignsData.data) {
          campaignsData.data.forEach(c => {
            campaignMap[c.id] = c.name;
          });
        }
        
        sentMessages.data.forEach(message => {
          const timestamp = message.sent_at || message.created_at;
          const leadUsername = message.instagram_conversation_id ? 'user' : 'unknown';
          const campaignName = campaignMap[message.campaign_id] || 'Unknown Campaign';
          
          activities.push({
            id: `message-sent-${message.id}`,
            type: 'lead_messaged',
            text: `Messaged @${leadUsername} in campaign "${campaignName}"`,
            time: formatTimeAgo(timestamp),
            color: 'blue'
          });
        });
      }
      
      // Get recent responses (lead responded activities)
      const responses = await supabase
        .from('received_responses')
        .select(`
          id,
          message_text,
          received_at,
          campaign_id,
          instagram_conversation_id
        `)
        .order('received_at', { ascending: false })
        .limit(Math.floor(limit / 3));
      
      if (responses.data) {
        // Get campaign names for the responses
        const campaignIds = [...new Set(responses.data.map(r => r.campaign_id).filter(Boolean))];
        const campaignsData = campaignIds.length > 0 ? await supabase
          .from('campaigns')
          .select('id, name')
          .in('id', campaignIds) : { data: [] };
        
        const campaignMap = {};
        if (campaignsData.data) {
          campaignsData.data.forEach(c => {
            campaignMap[c.id] = c.name;
          });
        }
        
        responses.data.forEach(response => {
          const leadUsername = response.instagram_conversation_id ? 'user' : 'unknown';
          const campaignName = campaignMap[response.campaign_id] || 'Unknown Campaign';
          
          activities.push({
            id: `response-${response.id}`,
            type: 'lead_responded',
            text: `@${leadUsername} responded in campaign "${campaignName}"`,
            time: formatTimeAgo(response.received_at),
            color: 'green'
          });
        });
      }
      
      // Get recent follow-up messages (using a simple filter for now)
      // For now, let's skip follow-ups as separate activity and include them in sent messages
      // This can be enhanced later when we have proper message type tracking
      
      // Get recent leads list creation
      const leadsLists = await supabase
        .from('leads_lists')
        .select('id, name, created_at, total_leads')
        .order('created_at', { ascending: false })
        .limit(Math.floor(limit / 3));
      
      if (leadsLists.data) {
        leadsLists.data.forEach(list => {
          activities.push({
            id: `leads-list-${list.id}`,
            type: 'leads_list_created',
            text: `Created leads list "${list.name}" with ${list.total_leads || 0} leads`,
            time: formatTimeAgo(list.created_at),
            color: 'orange'
          });
        });
      }
      
      // Get recent campaigns (created or started) - reduced limit since we have more activity types
      const campaigns = await supabase
        .from('campaigns')
        .select('id, name, status, created_at, started_at')
        .order('created_at', { ascending: false })
        .limit(Math.floor(limit / 3));
      
      if (campaigns.data) {
        campaigns.data.forEach(campaign => {
          // Add campaign creation activity
          activities.push({
            id: `campaign-created-${campaign.id}`,
            type: 'campaign_created',
            text: `Created new campaign "${campaign.name}"`,
            time: formatTimeAgo(campaign.created_at),
            color: 'cyan'
          });
          
          // Add campaign started activity if it has been started
          if (campaign.started_at && campaign.status === 'active') {
            activities.push({
              id: `campaign-started-${campaign.id}`,
              type: 'campaign_started',
              text: `Started campaign "${campaign.name}"`,
              time: formatTimeAgo(campaign.started_at),
              color: 'indigo'
            });
          }
        });
      }
      
      // Sort all activities by actual timestamp for proper chronological order
      activities.sort((a, b) => {
        // Extract the actual timestamp for proper sorting
        const getTimestamp = (timeStr) => {
          if (timeStr === 'Just now') return Date.now();
          if (timeStr.includes('minutes ago')) {
            const mins = parseInt(timeStr);
            return Date.now() - (mins * 60 * 1000);
          }
          if (timeStr.includes('hours ago')) {
            const hours = parseInt(timeStr);
            return Date.now() - (hours * 60 * 60 * 1000);
          }
          if (timeStr.includes('days ago')) {
            const days = parseInt(timeStr);
            return Date.now() - (days * 24 * 60 * 60 * 1000);
          }
          return new Date(timeStr).getTime();
        };
        
        return getTimestamp(b.time) - getTimestamp(a.time);
      });
      
      return activities.slice(0, limit);
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      throw error;
    }
  }
};

// =====================================================
// CAMPAIGN LEADS LISTS SERVICES
// =====================================================

export const campaignLeadsListService = {
  // Add multiple leads lists to campaign
  async addLeadsListsToCampaign(campaignId, leadsListIds) {
    if (!leadsListIds || leadsListIds.length === 0) return [];

    const relationships = leadsListIds.map(listId => ({
      campaign_id: campaignId,
      leads_list_id: listId
    }));

    const { data, error } = await supabase
      .from('campaign_leads_lists')
      .insert(relationships)
      .select();
    
    if (error) throw error;
    return data;
  },

  // Remove leads list from campaign
  async removeLeadsListFromCampaign(campaignId, leadsListId) {
    const { error } = await supabase
      .from('campaign_leads_lists')
      .delete()
      .eq('campaign_id', campaignId)
      .eq('leads_list_id', leadsListId);
    
    if (error) throw error;
  },

  // Get leads lists for campaign
  async getCampaignLeadsLists(campaignId) {
    const { data, error } = await supabase
      .from('campaign_leads_lists')
      .select(`
        *,
        leads_lists (
          id,
          name,
          total_leads
        )
      `)
      .eq('campaign_id', campaignId);
    
    if (error) throw error;
    return data;
  }
};

// =====================================================
// LEADS LISTS SERVICES
// =====================================================

export const leadsListService = {
  // Get all leads lists for user
  async getLeadsLists() {
    const { data, error } = await supabase
      .from('leads_lists')
      .select(`
        *,
        total_leads
      `)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  // Create new leads list
  async createLeadsList(listData) {
    const { data, error } = await supabase
      .from('leads_lists')
      .insert([{
        name: listData.name,
        description: listData.description || '',
        user_id: (await supabase.auth.getUser()).data.user?.id
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Import leads from CSV data - Using Edge Function for better performance
  async importLeadsFromCSV(listId, csvData) {
    try {
      // Get the current session token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      console.log(`📊 Importing leads to list ${listId}...`);

      // Call the edge function for bulk import
      const response = await fetch(`${supabaseUrl}/functions/v1/bulk-import-leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          listId,
          csvData
        })
      });

      const result = await response.json();
      
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to import leads');
      }

      console.log(`✅ Successfully imported ${result.imported} leads (${result.duplicates} duplicates skipped)`);
      console.log(`📊 Total leads in list: ${result.totalInList}`);

      // Update the total_leads count for the list
      if (result.totalInList !== null) {
        await supabase
          .from('leads_lists')
          .update({ 
            total_leads: result.totalInList,
            updated_at: new Date().toISOString()
          })
          .eq('id', listId);
      }

      return {
        success: true,
        totalImported: result.imported,
        duplicatesSkipped: result.duplicates,
        totalInList: result.totalInList
      };
      
    } catch (error) {
      console.error('Error importing leads:', error);
      throw error;
    }
  },

  // Get leads from a specific list
  async getLeadsFromList(listId, limit = 100, offset = 0) {
    const { data, error } = await supabase
      .from('leads_list_items')
      .select('*')
      .eq('leads_list_id', listId)
      .range(offset, offset + limit - 1)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  // Add individual lead to a list
  async addLeadToList(listId, username) {
    const { data, error } = await supabase
      .from('leads_list_items')
      .insert({
        leads_list_id: listId,
        instagram_username: username.toLowerCase()
        // Note: display_name can be stored in scraped_data if needed
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Remove individual lead from a list
  async removeLeadFromList(leadId) {
    const { error } = await supabase
      .from('leads_list_items')
      .delete()
      .eq('id', leadId);
    
    if (error) throw error;
  },

  // Update individual lead
  async updateLead(leadId, updates) {
    const { data, error } = await supabase
      .from('leads_list_items')
      .update(updates)
      .eq('id', leadId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Delete leads list
  async deleteLeadsList(id) {
    const { error } = await supabase
      .from('leads_lists')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  // Update leads list
  async updateLeadsList(id, updates) {
    const { data, error } = await supabase
      .from('leads_lists')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  }
};

// =====================================================
// SUBSCRIPTION SERVICES
// =====================================================

export const subscriptionService = {
  // Check if user can add more accounts to campaigns
  async canAddAccountToCampaign() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get user's subscription info
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('subscription_tier, max_active_accounts, subscription_status')
      .eq('id', user.id)
      .single();

    if (profileError) throw profileError;

    // Get current account usage in campaigns for this user only
    const { data: userCampaigns, error: campaignsError } = await supabase
      .from('campaigns')
      .select('id')
      .eq('user_id', user.id);

    if (campaignsError) throw campaignsError;

    const campaignIds = userCampaigns?.map(c => c.id) || [];
    
    const { data: currentAccounts, error: accountsError } = await supabase
      .from('campaign_accounts')
      .select('instagram_account_id', { count: 'exact' })
      .eq('is_enabled', true)
      .in('campaign_id', campaignIds.length > 0 ? campaignIds : ['']);

    if (accountsError) throw accountsError;

    const currentUsage = currentAccounts?.length || 0;
    const tier = profile.subscription_tier || 'free';
    const limit = profile.max_active_accounts || 0;
    const status = profile.subscription_status || 'inactive';

    // If limit is 0, user cannot add any accounts
    if (limit === 0) {
      return {
        allowed: false,
        tier,
        currentUsage,
        limit,
        message: 'Upgrade to a paid plan to add accounts to campaigns',
        upgradeRequired: true
      };
    }

    // Scale tier has unlimited accounts (very high limit)
    if (tier === 'scale' || limit >= 999999) {
      return {
        allowed: true,
        tier,
        currentUsage,
        limit: 'unlimited'
      };
    }

    // Check if user has reached their limit
    if (currentUsage >= limit) {
      return {
        allowed: false,
        tier,
        currentUsage,
        limit,
        message: tier === 'growth' 
          ? `You've used ${currentUsage} of ${limit} accounts. Increase your quantity or upgrade to Scale for unlimited accounts.`
          : `You've reached your limit of ${limit} account${limit > 1 ? 's' : ''}. Upgrade to add more accounts.`,
        upgradeRequired: tier === 'starter',
        canIncreaseQuantity: tier === 'growth'
      };
    }

    return {
      allowed: true,
      tier,
      currentUsage,
      limit,
      remaining: limit - currentUsage
    };
  },

  // Get current subscription status
  async getSubscriptionStatus() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data: profile, error } = await supabase
      .from('profiles')
      .select(`
        subscription_tier,
        subscription_status,
        subscription_accounts_limit,
        subscription_quantity,
        subscription_current_period_end,
        subscription_cancel_at_period_end
      `)
      .eq('id', user.id)
      .single();

    if (error) throw error;
    return profile;
  }
};